import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Sentry SDK init for `apps/web` (React).
 *
 * Per `plans/03-architecture/observability.md` + Web Wave 9 prompt:
 * - Browser tracing + replay (privacy-tight) integrations.
 * - `tracesSampleRate: 0.2`, `replaysSessionSampleRate: 0.1`,
 *   `replaysOnErrorSampleRate: 1.0`.
 * - `beforeSend` strips secret headers, query-string tokens, and any field
 *   matching the `redactFields()` allowlist before the event leaves the
 *   browser.
 * - `setUser` only ever sets `id` (Barel userId). Email + IP never leave.
 * - Source-map upload is gated on `SENTRY_AUTH_TOKEN` at build time (handled
 *   by `@sentry/vite-plugin` in `apps/web/vite.config.ts`).
 *
 * The init is **a no-op when `dsn` is falsy** so the same shell code works in
 * unconfigured local-dev / E2E environments.
 */
import * as Sentry from '@sentry/react';
import { redactFields, redactHeaders, redactUrl } from './redact.js';
/**
 * Apply the redaction allowlist to a Sentry envelope: scrub headers, strip
 * URL query tokens, and redact any matching field anywhere in the payload
 * (extra, contexts, request data).
 */
export function applyRedaction(event) {
    if (event.request) {
        if (event.request.url) {
            event.request.url = redactUrl(event.request.url);
        }
        if (event.request.headers) {
            event.request.headers = redactHeaders(event.request.headers);
        }
        if (event.request.cookies !== undefined) {
            // Cookie redaction — replace ALL cookie values with '[redacted]' but
            // keep the key set so dashboards can still see which cookies were
            // present at error time. Sentry's `Request.cookies` is keyed.
            const orig = event.request.cookies;
            const cleaned = {};
            if (typeof orig === 'string') {
                // Older shape — coerce into a single sentinel key.
                cleaned['_'] = '[redacted]';
            }
            else {
                for (const k of Object.keys(orig))
                    cleaned[k] = '[redacted]';
            }
            event.request.cookies = cleaned;
        }
        if (typeof event.request.query_string === 'string') {
            event.request.query_string = redactUrl(`?${event.request.query_string}`).replace(/^\?/, '');
        }
    }
    if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => scrubBreadcrumb(bc));
    }
    if (event.extra) {
        event.extra = redactFields(event.extra);
    }
    if (event.contexts) {
        event.contexts = redactFields(event.contexts);
    }
    if (event.tags) {
        event.tags = redactFields(event.tags);
    }
    // Belt-and-braces — never let an `email` / `ip_address` slip through. We
    // only ever want `id` on the user payload.
    if (event.user) {
        const { id } = event.user;
        event.user = id ? { id } : undefined;
    }
    return event;
}
function scrubBreadcrumb(bc) {
    const next = { ...bc };
    if (next.data) {
        const data = { ...next.data };
        if (typeof data['url'] === 'string')
            data['url'] = redactUrl(data['url']);
        if (typeof data['http.url'] === 'string')
            data['http.url'] = redactUrl(data['http.url']);
        if (data['headers'] && typeof data['headers'] === 'object') {
            data['headers'] = redactHeaders(data['headers']);
        }
        next.data = redactFields(data);
    }
    if (typeof next.message === 'string') {
        // Drop bare bearer-looking tokens from breadcrumb messages.
        next.message = next.message.replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [redacted]');
    }
    return next;
}
/**
 * Initialise the Sentry browser SDK. Returns `true` if a client was set up,
 * `false` otherwise.
 */
export function initSentryWeb(options) {
    const dsn = options.dsn?.trim();
    if (!dsn)
        return false;
    const integrations = [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            // Privacy posture — even at 0.1 sample rate, mask everything by default.
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
        }),
    ];
    Sentry.init({
        dsn,
        environment: options.env,
        release: options.release,
        tracesSampleRate: options.tracesSampleRate ?? 0.2,
        replaysSessionSampleRate: options.replaysSessionSampleRate ?? 0.1,
        replaysOnErrorSampleRate: options.replaysOnErrorSampleRate ?? 1.0,
        sendDefaultPii: false,
        integrations,
        beforeSend(event, _hint) {
            return applyRedaction(event);
        },
        beforeBreadcrumb(bc, _hint) {
            return scrubBreadcrumb(bc);
        },
    });
    return true;
}
/**
 * Set the current user on the Sentry scope. Only `id` is recorded — never
 * `email`, `username`, or `ip_address`.
 */
export function setSentryUser(userId) {
    if (!userId) {
        Sentry.setUser(null);
        return;
    }
    Sentry.setUser({ id: userId });
}
/**
 * Thin wrapper around `Sentry.ErrorBoundary` that aligns with our prop shape
 * and is safe to call when `initSentryWeb` was a no-op (Sentry's own boundary
 * still renders the fallback even without a client).
 */
export function SentryErrorBoundary(props) {
    const { children, fallback } = props;
    if (typeof fallback === 'function') {
        const FallbackComponent = fallback;
        return (_jsx(Sentry.ErrorBoundary, { fallback: ({ error, resetError }) => (_jsx(FallbackComponent, { error: error, resetError: resetError })), children: children }));
    }
    return _jsx(Sentry.ErrorBoundary, { fallback: fallback, children: children });
}
/** Re-export of `Sentry.ErrorBoundary` for callers that need raw API. */
export const RawSentryErrorBoundary = Sentry.ErrorBoundary;
//# sourceMappingURL=sentry-web.js.map